import { motion, useMotionValue, useTransform, animate, MotionValue } from "framer-motion";
import { Database, Users, TrendingUp, CheckCircle, Euro, ShoppingCart, Coffee, Clock, CalendarClock } from "lucide-react";
import { useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { MOCK_EMPLOYEES, MOCK_BUSYNESS } from "./mockData";

interface Step2POSProps {
  scrollProgress: MotionValue<number>;
}

// Animated counter component
const AnimatedCounter = ({ value, prefix = "", suffix = "" }: { value: number; prefix?: string; suffix?: string }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => {
    return prefix + Math.round(latest).toLocaleString() + suffix;
  });

  useEffect(() => {
    const controls = animate(count, value, {
      duration: 1.5,
      ease: "easeOut",
    });
    return controls.stop;
  }, [count, value]);

  return <motion.span>{rounded}</motion.span>;
};

// Employee Card Component - Fluid responsive version
const EmployeeCard = ({ employee, index, className = "flex" }: { employee: typeof MOCK_EMPLOYEES[0]; index: number; className?: string }) => {
  const initials = employee.name
    .split(" ")
    .map((n) => n[0])
    .join("");

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        delay: index * 0.08,
        duration: 0.3,
        type: "spring",
      }}
      className={`${className} flex-col items-center justify-center rounded-lg bg-muted/30 border border-border hover:border-border/80 transition-colors w-full h-full`}
      style={{ padding: '8%', gap: '4%' }}
    >
      {/* Avatar - scales with container */}
      <div
        className="rounded-full flex items-center justify-center font-semibold text-white shrink-0 aspect-square"
        style={{
          backgroundColor: employee.color,
          width: '45%',
          maxWidth: '2.5rem',
          fontSize: '0.65em'
        }}
      >
        {initials}
      </div>
      {/* Info - fluid text */}
      <div className="text-center min-w-0 w-full" style={{ fontSize: 'clamp(0.5rem, 1.5vw, 0.75rem)' }}>
        <p className="font-medium text-foreground truncate">
          {employee.name.split(" ")[0]}
        </p>
        <p className="text-muted-foreground truncate" style={{ fontSize: '0.85em' }}>{employee.role}</p>
      </div>
    </motion.div>
  );
};

// Time tracking entry for visualization
const TimeTrackingEntry = ({ employee, index }: { employee: typeof MOCK_EMPLOYEES[0]; index: number }) => {
  const clockIn = `${8 + index}:${index % 2 === 0 ? '00' : '30'}`;
  const clockOut = `${16 + index}:${index % 2 === 0 ? '30' : '00'}`;
  const hours = 8 + (index % 2 === 0 ? 0.5 : -0.5);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.8 + index * 0.1, duration: 0.4 }}
      className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border"
    >
      <div className="flex items-center gap-2">
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white"
          style={{ backgroundColor: employee.color }}
        >
          {employee.name.split(" ").map(n => n[0]).join("")}
        </div>
        <span className="text-sm text-foreground">{employee.name.split(" ")[0]}</span>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span className="text-emerald-600 dark:text-emerald-400">{clockIn}</span>
        <span className="text-muted-foreground">→</span>
        <span className="text-rose-600 dark:text-rose-400">{clockOut}</span>
        <span className="text-muted-foreground font-medium">{hours}h</span>
      </div>
    </motion.div>
  );
};

export const Step2POS = ({ scrollProgress }: Step2POSProps) => {
  // Map scroll progress to reveal different elements
  const employeesOpacity = useTransform(scrollProgress, [0, 0.2], [0, 1]);
  const timeTrackingOpacity = useTransform(scrollProgress, [0.15, 0.35], [0, 1]);
  const analyticsOpacity = useTransform(scrollProgress, [0.3, 0.5], [0, 1]);
  const statsOpacity = useTransform(scrollProgress, [0.45, 0.65], [0, 1]);
  const successOpacity = useTransform(scrollProgress, [0.75, 0.95], [0, 1]);

  return (
    <div className="max-w-7xl mx-auto w-full h-full flex flex-col py-6">
      {/* 2 Column Layout */}
      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">

        {/* Left Column: Employee Import + Time Tracking */}
        <div className="flex flex-col gap-4 min-h-0 overflow-hidden">
          {/* Employee Import - Bigger */}
          <motion.div
            style={{ opacity: employeesOpacity }}
            className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4 flex-1 flex flex-col min-h-0 overflow-hidden"
          >
            <div className="flex items-center justify-between mb-3 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/10">
                  <Users className="w-5 h-5 text-violet-500 dark:text-violet-400" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground mb-0.5">
                    Employee Import
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Team synchronized from your POS
                  </p>
                </div>
              </div>
              <motion.div style={{ opacity: successOpacity }}>
                <CheckCircle className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
              </motion.div>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 flex-1">
              {MOCK_EMPLOYEES.slice(0, 5).map((employee, index) => {
                // Responsive visibility: 1 row only, match employee count to columns
                // 3 cols (default), 4 cols (sm), 3 cols (md), 4 cols (lg), 5 cols (xl)
                let visibilityClass: string;

                if (index <= 2) {
                  // First 3: always visible
                  visibilityClass = "flex";
                } else if (index === 3) {
                  // 4th: visible on sm, hidden on md, visible on lg+
                  visibilityClass = "hidden sm:flex md:hidden lg:flex";
                } else {
                  // 5th: only visible on xl
                  visibilityClass = "hidden xl:flex";
                }

                return (
                  <EmployeeCard
                    key={employee.name}
                    employee={employee}
                    index={index}
                    className={visibilityClass}
                  />
                );
              })}
            </div>

            <motion.div
              style={{ opacity: successOpacity }}
              className="mt-3 p-2 rounded-lg bg-violet-500/10 border border-violet-500/20 shrink-0"
            >
              <p className="text-xs text-violet-600 dark:text-violet-400 font-medium">
                {MOCK_EMPLOYEES.length} employees imported ✓
              </p>
            </motion.div>
          </motion.div>

          {/* Time Tracking Import */}
          <motion.div
            style={{ opacity: timeTrackingOpacity }}
            className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4 shrink-0"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <CalendarClock className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground mb-0.5">
                    Time Tracking
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Attendance synced
                  </p>
                </div>
              </div>
              <motion.div style={{ opacity: successOpacity }}>
                <CheckCircle className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
              </motion.div>
            </div>

            <div className="space-y-1">
              {MOCK_EMPLOYEES.slice(0, 2).map((employee, index) => (
                <TimeTrackingEntry
                  key={employee.name}
                  employee={employee}
                  index={index}
                />
              ))}
            </div>

            <motion.div
              style={{ opacity: successOpacity }}
              className="mt-3 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center"
            >
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                <Clock className="w-3.5 h-3.5 inline mr-1" />
                24.5h logged
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Right Column: Analytics (Busyness + Sales merged) */}
        <motion.div
          style={{ opacity: analyticsOpacity }}
          className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4 flex flex-col min-h-0 overflow-hidden"
        >
          <div className="flex items-center gap-3 mb-3 shrink-0">
            <div className="p-2 rounded-lg bg-cyan-500/10">
              <TrendingUp className="w-5 h-5 text-cyan-500 dark:text-cyan-400" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-foreground mb-0.5">
                Business Analytics
              </h4>
              <p className="text-xs text-muted-foreground">Sales & busyness insights</p>
            </div>
          </div>

          {/* Revenue Stats Row */}
          <motion.div
            style={{ opacity: statsOpacity }}
            className="grid grid-cols-3 gap-3 mb-3 shrink-0"
          >
            <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
              <p className="text-xs text-muted-foreground mb-0.5">Revenue</p>
              <p className="text-base font-bold text-emerald-600 dark:text-emerald-400">
                <AnimatedCounter value={MOCK_BUSYNESS.dailyRevenue} prefix="€" />
              </p>
            </div>
            <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
              <p className="text-xs text-muted-foreground mb-0.5">Orders</p>
              <p className="text-base font-bold text-blue-600 dark:text-blue-400">
                <AnimatedCounter value={MOCK_BUSYNESS.ordersToday} />
              </p>
            </div>
            <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
              <p className="text-xs text-muted-foreground mb-0.5">Avg</p>
              <p className="text-base font-bold text-amber-600 dark:text-amber-400">
                <AnimatedCounter value={MOCK_BUSYNESS.averageOrder} prefix="€" />
              </p>
            </div>
          </motion.div>

          {/* Busyness Chart */}
          <div className="flex-1 min-h-[100px]">
            <p className="text-xs text-muted-foreground mb-1">Hourly Order Volume</p>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={MOCK_BUSYNESS.hourlyOrders}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border"
                  vertical={false}
                />
                <XAxis
                  dataKey="hour"
                  className="text-muted-foreground"
                  tick={{ fill: "currentColor", fontSize: 11 }}
                  axisLine={{ className: "stroke-border" }}
                />
                <YAxis
                  className="text-muted-foreground"
                  tick={{ fill: "currentColor", fontSize: 11 }}
                  axisLine={{ className: "stroke-border" }}
                  width={30}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    color: "hsl(var(--foreground))",
                  }}
                  cursor={{ fill: "rgba(6, 182, 212, 0.1)" }}
                />
                <Bar
                  dataKey="orders"
                  fill="#06b6d4"
                  radius={[6, 6, 0, 0]}
                  animationDuration={1500}
                  animationBegin={600}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Peak Hours & Top Product */}
          <motion.div
            style={{ opacity: statsOpacity }}
            className="mt-2 flex items-center justify-between text-xs shrink-0"
          >
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-cyan-500" />
              <span className="text-muted-foreground">Peak:</span>
              <span className="text-cyan-600 dark:text-cyan-400 font-medium">12:00 & 20:00</span>
            </div>
            <div className="flex items-center gap-1">
              <Coffee className="w-3 h-3 text-amber-500" />
              <span className="text-muted-foreground">Top:</span>
              <span className="text-amber-600 dark:text-amber-400 font-medium">{MOCK_BUSYNESS.topProduct}</span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Success Banner */}
      <motion.div
        style={{ opacity: successOpacity }}
        className="mt-4 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20 shrink-0"
      >
        <div className="flex items-center justify-center gap-2">
          <Database className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">
            POS integration complete!
          </p>
        </div>
      </motion.div>
    </div>
  );
};
